import {
    useCallback,
    useEffect,
    useRef,
    type PointerEventHandler,
    type RefObject,
} from "react";

interface UseTextAreaResizeProps {
    ref: RefObject<HTMLTextAreaElement | null>;
    isEnabled: boolean;
}

/** Handles the custom resize grip's vertical pointer drag. */
export const useTextAreaResize = ({
    ref,
    isEnabled,
}: UseTextAreaResizeProps) => {
    const cleanupRef = useRef<(() => void) | null>(null);

    const onPointerDown: PointerEventHandler<HTMLSpanElement> = useCallback(
        (event) => {
            if (!isEnabled) {
                return;
            }
            const textArea = ref.current;
            if (!textArea) {
                return;
            }

            event.preventDefault();
            cleanupRef.current?.();

            const startY = event.clientY;
            const startHeight = textArea.offsetHeight;
            const computedMinHeight = Number.parseFloat(
                getComputedStyle(textArea).minHeight,
            );
            const minHeight = Number.isFinite(computedMinHeight)
                ? computedMinHeight
                : 0;

            const handlePointerMove = (pointerEvent: PointerEvent) => {
                const nextHeight = Math.max(
                    startHeight + pointerEvent.clientY - startY,
                    minHeight,
                );
                textArea.style.height = `${nextHeight}px`;
            };

            const cleanup = () => {
                window.removeEventListener("pointermove", handlePointerMove);
                window.removeEventListener("pointerup", cleanup);
                cleanupRef.current = null;
            };

            cleanupRef.current = cleanup;
            window.addEventListener("pointermove", handlePointerMove);
            window.addEventListener("pointerup", cleanup);
        },
        [isEnabled, ref],
    );

    useEffect(() => {
        if (!isEnabled) {
            cleanupRef.current?.();
            if (ref.current) {
                ref.current.style.height = "";
            }
        }
        return () => {
            cleanupRef.current?.();
        };
    }, [isEnabled, ref]);

    return { gripProps: { onPointerDown } };
};
